// PrimaryNavMega — F9.6 WAVE 2F · dropdown panel for one top-level trigger.
//
// Props:
//   • items: array of {key, labelKey, icon, to, module?} from menuGroups
//   • onItemClick: () => void (closes panel from parent)
//   • isAdmin: boolean (bypass plan gating for admins)
//   • hasModule: (slug) => boolean (plan-entitlement check from AuthContext)
//
// Rendering:
//   • single-column list when <=5 items, 2-column grid when 6+
//   • Items without entitlement are shown greyed-out with a Lock badge
//   • Active route is highlighted
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import LockTooltip from "./LockTooltip";

export default function PrimaryNavMega({ items, onItemClick, isAdmin, hasModule, groupKey }) {
    const { t } = useTranslation("header_v3");
    const location = useLocation();
    const twoCols = items.length > 5;

    return (
        <div
            className="rounded-xl border p-2 shadow-[0_24px_60px_rgba(0,0,0,0.6)]"
            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A", minWidth: twoCols ? 480 : 260 }}
            role="menu"
        >
            <ul className={`grid ${twoCols ? "grid-cols-2 gap-x-2" : "grid-cols-1"} gap-y-0.5`}>
                {items.map((item) => {
                    const locked = !isAdmin && item.module && !hasModule(item.module);
                    const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
                    const Icon = item.icon;
                    const base = "group flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-colors";

                    if (locked) {
                        return (
                            <li key={item.key}>
                                <LockTooltip item={item} groupKey={groupKey}>
                                    <span
                                        role="menuitem"
                                        aria-disabled="true"
                                        data-testid={`primary-nav-item-${item.key}`}
                                        className={`${base} text-[#999999] cursor-not-allowed`}
                                    >
                                        <Icon className="w-4 h-4 opacity-60" />
                                        <span className="flex-1 truncate">{t(item.labelKey)}</span>
                                        <Lock className="w-3 h-3" aria-hidden />
                                    </span>
                                </LockTooltip>
                            </li>
                        );
                    }
                    return (
                        <li key={item.key}>
                            <Link
                                role="menuitem"
                                data-testid={`primary-nav-item-${item.key}`}
                                to={item.to}
                                onClick={onItemClick}
                                className={
                                    active
                                        ? `${base} font-medium`
                                        : `${base} text-[#E5E5E5] hover:text-white hover:bg-white/[0.06]`
                                }
                                style={
                                    active
                                        ? { backgroundColor: "rgba(255, 255, 255,0.12)", color: "#FFFFFF" }
                                        : undefined
                                }
                                aria-current={active ? "page" : undefined}
                            >
                                <Icon className="w-4 h-4" style={active ? { color: "#FFFFFF" } : undefined} />
                                <span className="flex-1 truncate">{t(item.labelKey)}</span>
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
